
<ng-template #encryptedImportDialog>
  <h2 mat-dialog-title>Encrypted File</h2>
  <mat-dialog-content>
    File is encrypted. Check GEA Sensitivity setting and change to a non-encrypted setting to import.
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    <button type="button" mat-button mat-dialog-close>Close</button>
  </mat-dialog-actions>
</ng-template>

<ng-template #importColumnMappingDialog>
  <h2 mat-dialog-title>Match Import Columns</h2>
  <mat-dialog-content class="import-column-mapping">
    <div class="import-mapping-row import-mapping-heading">
      <span>CSV / Excel Column</span>
      <span>Data Column</span>
      <span>Units</span>
    </div>
    <div class="import-mapping-row" *ngFor="let pair of importColumnMappings; let index = index">
      <span>{{ unmatchedImportColumns[index].label }}</span>
      <select [(ngModel)]="pair.targetName" (ngModelChange)="onImportMappingTargetChange(pair)"
        [class.import-column-skipped]="!pair.targetName" aria-label="Data column">
        <option value="" class="import-column-skipped">Skip column</option>
        <option *ngFor="let column of getAvailableImportDataColumns(pair)" [ngValue]="column.name">{{ column.label }}</option>
      </select>
      <div>
        <select *ngIf="getImportMeasurementColumn(pair) as column" [(ngModel)]="pair.unitId" aria-label="Import units">
          <option [ngValue]="null"></option>
          <option *ngFor="let option of getUnitOptionsForContext(column.ContextId)" [ngValue]="option.id">{{ option.label }}</option>
        </select>
      </div>
    </div>
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    <button type="button" mat-button [mat-dialog-close]="false">Cancel</button>
    <button type="button" mat-stroked-button [mat-dialog-close]="true" [disabled]="!importMappingsValid">Continue</button>
  </mat-dialog-actions>
</ng-template>

<ng-template #importReportDialog>
  <h2 mat-dialog-title>Import Report</h2>
  <mat-dialog-content *ngIf="importReport as report" class="import-report">
    <dl>
      <dt>Rows Imported (Processed)</dt><dd>{{ report.imported }}</dd>
      <dt>Rows Ignored (No Update)</dt><dd>{{ report.ignored }}</dd>
      <dt>Rows Updated</dt><dd>{{ report.updated }}</dd>
      <dt>Rows Added</dt><dd>{{ report.added }}</dd>
      <dt>Rows Failed</dt><dd>{{ report.failed.length }}</dd>
    </dl>
    <table *ngIf="report.failed.length">
      <thead><tr><th>ID</th><th>Error</th></tr></thead>
      <tbody><tr *ngFor="let failure of report.failed"><td>{{ failure.id }}</td><td>{{ failure.error }}</td></tr></tbody>
    </table>
    <h3>Valid Columns</h3>
    <p>{{ report.validColumns.join(', ') || 'None' }}</p>
    <h3>Invalid Columns (Ignored)</h3>
    <p>{{ report.invalidColumns.join(', ') || 'None' }}</p>
    <h3>Skipped Columns (Not In File)</h3>
    <p>{{ report.skippedColumns.join(', ') || 'None' }}</p>
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    <button type="button" mat-button mat-dialog-close>Close</button>
  </mat-dialog-actions>
</ng-template>

<div class="mode-selector" role="radiogroup" aria-label="Mode">
  <label class="mode-option"><input type="radio" name="mode" value="tables" [(ngModel)]="mode" (ngModelChange)="onModeChange($event)"><span>Table Data</span></label>
  <label class="mode-option"><input type="radio" name="mode" value="enums" [(ngModel)]="mode" (ngModelChange)="onModeChange($event)"><span>Enums</span></label>
  <label class="mode-option"><input type="radio" name="mode" value="tableEditor" [(ngModel)]="mode" (ngModelChange)="onModeChange($event)"><span>Table Editor</span></label>
  <span *ngIf="pageStatusMessage" class="page-status-message" [class.error]="pageStatusIsError"
    role="status">{{ pageStatusMessage }}</span>
</div>

<div *ngIf="mode === 'tables'" class="engineering-table-mode">
  <div #engineeringTableControls class="engineering-table-controls">
  <div class="table-selectors">
    <label class="table-selector">
      <span>Table</span>
      <select [(ngModel)]="selectedTable" (ngModelChange)="onTableChange($event)">
        <option *ngFor="let tbl of tables" [ngValue]="tbl">{{ variableToDisplay(tbl.Name) }}</option>
      </select>
    </label>

    <label class="table-selector">
      <span>Current Version</span>
      <select [(ngModel)]="currentChangeset" (ngModelChange)="onChangesetChange($event)">
        <option *ngFor="let cs of changesets" [ngValue]="cs">{{ cs.TableChangesetId + " (by " + cs.UserName + " on " + formatDate(cs.Created) + ")" }}</option>
      </select>
    </label>

    <label class="table-selector">
      <span>Compare To</span>
      <select [(ngModel)]="compareChangeset" (ngModelChange)="onCompareChange($event)">
        <option *ngFor="let cs of comparableChangesets" [ngValue]="cs">{{ cs.TableChangesetId + " (by " + cs.UserName + " on " + formatDate(cs.Created) + ")" }}</option>
      </select>
    </label>
  </div>

  <div class="table-actions-toolbar">
    <div class="table-action-group" [class.has-chips]="activeFilterChips.length > 0">
      <button type="button" mat-stroked-button (click)="openFilterPopup(filterDialog)">
        <mat-icon>filter_list</mat-icon>
        Filters
      </button>
      <div class="filter-chips" *ngIf="activeFilterChips.length">
        <span class="filter-chip" *ngFor="let chip of activeFilterChips">
          <span>{{ chip.text }}</span>
          <button type="button" mat-icon-button [attr.aria-label]="'Remove filter ' + chip.text"
            title="Remove filter" (click)="removeFilterChip(chip.key)">
            <mat-icon>close</mat-icon>
          </button>
        </span>
        <button type="button" mat-stroked-button class="reset-filter-chip" (click)="clearFilters()">
          Reset All Filters
          <mat-icon>replay</mat-icon>
        </button>
      </div>
    </div>

    <div class="table-action-group" [class.has-chips]="activeSortChips.length > 0">
      <button type="button" mat-stroked-button (click)="openFilterPopup(sortDialog)">
        <mat-icon>sort</mat-icon>
        Sort
      </button>
      <div class="sort-chips" *ngIf="activeSortChips.length">
        <span class="filter-chip" *ngFor="let chip of activeSortChips; trackBy: trackFilterChip">
          <span>{{ chip.text }}</span>
          <button type="button" mat-icon-button [attr.aria-label]="'Remove sort ' + chip.text"
            title="Remove sort" (click)="removeSortChip(chip.key)">
            <mat-icon>close</mat-icon>
          </button>
        </span>
        <button type="button" mat-stroked-button class="reset-filter-chip" (click)="clearSorts()">
          Reset All Sorts
          <mat-icon>replay</mat-icon>
        </button>
      </div>
    </div>

    <button type="button" mat-stroked-button (click)="openColumnsPopup(columnsDialog)">
      <mat-icon>view_column</mat-icon>
      Columns
    </button>
    <span class="table-toolbar-separator" role="separator" aria-orientation="vertical"></span>
    <button type="button" mat-stroked-button (click)="saveView()">
      <mat-icon>save</mat-icon>
      Save View
    </button>
    <input #viewFileInput type="file" accept=".json,application/json" class="visually-hidden"
      (change)="loadView($event)" />
    <button type="button" mat-stroked-button (click)="viewFileInput.click()">
      <mat-icon>upload_file</mat-icon>
      Load View
    </button>
    <button type="button" mat-stroked-button (click)="resetView()">
      <mat-icon>restart_alt</mat-icon>
      Reset View
    </button>
  </div>

  </div>
  <ng-template #filterDialog>
    <h2 mat-dialog-title>Table Filters</h2>
    <mat-dialog-content class="engineering-filter-form">
      <label class="engineering-filter-row">
        <span>Enabled</span>
        <select [(ngModel)]="enabledFilter" (ngModelChange)="filtersChanged()">
          <option value="all">All</option>
          <option value="enabled">Enabled</option>
          <option value="disabled">Disabled</option>
        </select>
      </label>

      <div class="engineering-filter-rules">
        <div class="engineering-filter-rule" *ngFor="let filter of filterRules; trackBy: trackFilter">
          <select aria-label="Filter column" [(ngModel)]="filter.columnName"
            (ngModelChange)="onFilterColumnChanged(filter)">
            <option *ngFor="let column of filterColumns" [ngValue]="column.Name">{{ getDisplayHeading(column) }}</option>
          </select>

          <ng-container *ngIf="getColumnByName(filter.columnName) as column">
            <select aria-label="Filter operator" [(ngModel)]="filter.operator" (ngModelChange)="filtersChanged()">
              <option *ngFor="let operator of operatorsForColumn(column)" [ngValue]="operator">{{ operator }}</option>
            </select>

            <select *ngIf="column.TypeId === ColumnType.Enum" aria-label="Filter value" [(ngModel)]="filter.value" (ngModelChange)="filtersChanged()">
              <option [ngValue]="null">Select value</option>
              <option *ngFor="let option of getColumnEnumValues(column)" [ngValue]="option.CodeId">{{ option.Label }}</option>
            </select>
            <select *ngIf="column.TypeId === ColumnType.Boolean" aria-label="Filter value" [(ngModel)]="filter.value" (ngModelChange)="filtersChanged()">
              <option [ngValue]="null">Select value</option>
              <option [ngValue]="true">Yes</option>
              <option [ngValue]="false">No</option>
            </select>
            <input *ngIf="isStringColumn(column)" aria-label="Filter value" type="text" [(ngModel)]="filter.value" (ngModelChange)="filtersChanged()" />
            <input *ngIf="isNumericColumn(column)" aria-label="Filter value" type="number"
              [step]="column.TypeId === ColumnType.Int ? 1 : 'any'" [(ngModel)]="filter.value" (ngModelChange)="filtersChanged()" />
            <input *ngIf="column.TypeId === ColumnType.DateTime" aria-label="Filter value" type="date" [(ngModel)]="filter.value" (ngModelChange)="filtersChanged()" />
            <ng-container *ngIf="isBetween(filter); else noSecondFilterValue">
              <input aria-label="Filter upper value" type="number"
                [step]="column.TypeId === ColumnType.Int ? 1 : 'any'" [(ngModel)]="filter.value2" (ngModelChange)="filtersChanged()" />
            </ng-container>
            <ng-template #noSecondFilterValue><span></span></ng-template>
          </ng-container>

          <button type="button" mat-icon-button [attr.aria-label]="'Delete filter on ' + filter.columnName"
            title="Delete filter" (click)="removeFilter(filter.id)">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      </div>
      <button type="button" mat-stroked-button (click)="addFilter()" [disabled]="!columns.length">
        <mat-icon>add</mat-icon>
        Add Filter
      </button>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="clearFilters()">Clear Filters</button>
      <button mat-button type="button" mat-dialog-close>Close</button>
    </mat-dialog-actions>
  </ng-template>

  <ng-template #sortDialog>
    <h2 mat-dialog-title>Sort Table</h2>
    <mat-dialog-content class="engineering-sort-form">
      <div class="engineering-sort-rules">
        <div class="engineering-sort-rule" *ngFor="let rule of sortRules; trackBy: trackSortRule">
          <select aria-label="Sort column" [(ngModel)]="rule.columnKey" (ngModelChange)="sortRulesChanged()">
            <option *ngFor="let option of sortColumnOptions" [ngValue]="option.key">{{ option.label }}</option>
          </select>
          <select aria-label="Sort direction" [(ngModel)]="rule.direction" (ngModelChange)="sortRulesChanged()">
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
          <button type="button" mat-icon-button [attr.aria-label]="'Delete sort on ' + rule.columnKey"
            title="Delete sort" (click)="removeSortRule(rule.id)">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      </div>
      <button type="button" mat-stroked-button (click)="addSortRule()" [disabled]="!sortColumnOptions.length">
        <mat-icon>add</mat-icon>
        Add Sort
      </button>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="clearSorts()">Clear Sorts</button>
      <button mat-button type="button" mat-dialog-close>Close</button>
    </mat-dialog-actions>
  </ng-template>

  <ng-template #columnsDialog>
    <h2 mat-dialog-title>Visible Columns</h2>
    <mat-dialog-content class="engineering-columns-dialog">
      <div class="column-visibility-actions">
        <button type="button" mat-button (click)="selectAllColumns()">Select All</button>
        <button type="button" mat-button (click)="deselectAllColumns()">Deselect All</button>
      </div>
      <label class="column-visibility-option"><input type="checkbox" checked disabled /> ID</label>
      <label class="column-visibility-option"><input type="checkbox" checked disabled /> Enabled</label>
      <label class="column-visibility-option"><input type="checkbox" checked disabled /> Order</label>
      <label class="column-visibility-option" *ngFor="let column of columns">
        <input type="checkbox" [(ngModel)]="columnVisibility[column.Name]" />
        {{ getDisplayHeading(column) }}
      </label>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" mat-dialog-close>Close</button>
    </mat-dialog-actions>
  </ng-template>

  <div #engineeringTableScroll class="engineering-table-scroll">
    <table>
      <thead>
      <tr>
        <th>ID</th>
        <th>Enabled</th>
        <th>Order</th>
        <th *ngFor="let column of visibleColumns; trackBy: trackDataColumn">
          <span class="tooltip column-heading-tooltip">
            {{ getDisplayHeading(column) }}
            <span class="tooltiptext">
              <span><b>---Column Details---</b></span>
              <span><br><b>Name:</b> {{ column.Name }}</span>
              <span *ngIf="column.Description"><br><b>Description:</b> {{ column.Description }}</span>
              <span><br><b>Data Type:</b> {{ getTypeString(column) }}</span>
              <span *ngIf="column.DecimalDigits"><br><b>Decimal Digits:</b> {{ column.DecimalDigits }}</span>
              <span *ngIf="column.ContextId"><br><b>Measurement Type:</b> {{ getContextString(column) }}</span>
              <span *ngIf="column.UnitId"><br><b>Unit Type:</b> {{ getUnitString(column) }}</span>
              <span *ngIf="column.EnumType"><br><b>Enum Type:</b> {{ variableToDisplay(column.EnumType) }}</span>
            </span>
          </span>
          <ng-container *ngIf="getColumnUnitHeading(column) as units">
            <br />
            <span class="column-unit">{{ units }}</span>
          </ng-container>
        </th>
      </tr>
      </thead>
      <tbody>
      <!-- <tr>
        <td></td>
        <td></td>
        <td></td>
        <td *ngFor="let column of columns">
          <select *ngIf="column.TypeId === 6" [(ngModel)]="columnFilters[column.Name]" (ngModelChange)="filterChanged()">
            <option *ngFor="let option of getColumnEnumValues(column)" [ngValue]="option.CodeId">{{ option.Label }}</option>
          </select>
        </td>
      </tr> -->
      <tr *ngFor="let row of pagedData; trackBy: trackDataRow" draggable="true" (dragstart)="onDragStart($event, row)" (dragover)="onDragOver($event)" (drop)="onDrop($event, row)">
        <td [ngClass]="{'new': isDataNew(row)}">{{ row.DataId }}</td>
        <td class="enabled-cell" [ngClass]="{'modified': isDataModified(row, 'IsEnabled'), 'new': isDataNew(row)}">
          <div class="enabled-cell-content">
            <input type="checkbox" [(ngModel)]="row.IsEnabled" />
            <button type="button" mat-icon-button class="delete-new-row-button"
              *ngIf="isDataNew(row) && currentChangeset == compareChangeset"
              [attr.aria-label]="'Delete new row ' + row.DataId" title="Delete new row" (click)="deleteRow(row)">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        </td>
        <td [ngClass]="{'modified': isDataModified(row, 'SortOrder'), 'new': isDataNew(row)}">{{ row.SortOrder }}</td>
        <td *ngFor="let column of visibleColumns; trackBy: trackDataColumn" (click)="onCellClick(row, column)"
          [ngClass]="{'modified': isDataModified(row, column.Name), 'new': isDataNew(row)}"
          [class.editing-cell]="isEditingCell(row, column)"
          [class.boolean-editing-cell]="column.TypeId === ColumnType.Boolean && isEditingCell(row, column)">
          <span *ngIf="isEditingCell(row, column)">
            <input *ngIf="column.TypeId <= 3" [(ngModel)]="editValue" (ngModelChange)="cellChanged()" />
            <input *ngIf="column.TypeId === 4" type="datetime-local" [ngModel]="formatDateTimeInput(editValue)" (ngModelChange)="dateTimeChanged($event)" />
            <input *ngIf="column.TypeId === 5" type="checkbox" [(ngModel)]="editValue" (ngModelChange)="cellChanged()" />
            <select *ngIf="column.TypeId === 6" [(ngModel)]="editValue" (ngModelChange)="cellChanged()">
              <option *ngFor="let option of editEnumValues" [ngValue]="option.CodeId" >{{ option.Label }}</option>
            </select>
          </span>
          <span *ngIf="!isEditingCell(row, column)">
            <ng-container *ngIf="showsPreviousCellValue(column) && isDataModified(row, column.Name); else currentCellValue">
              <del class="previous-cell-value">{{ getOriginalDisplayValue(row, column) }}</del>
              <span>{{ getDisplayValue(row, column) }}</span>
            </ng-container>
            <ng-template #currentCellValue>{{ getDisplayValue(row, column) }}</ng-template>
          </span>
        </td>
      </tr>
      </tbody>
    </table>
  </div>

  <div #engineeringTableFooter class="table-toolbar">
    <span class="table-count">Total records: {{ currentData.length }}</span>
    <span class="table-toolbar-separator" role="separator" aria-orientation="vertical"></span>
    <span class="table-count">Filtered records: {{ filteredData.length }}</span>
    <span class="table-toolbar-separator" role="separator" aria-orientation="vertical"></span>
    <label class="table-toolbar-control">
      Page
      <select [ngModel]="pageNumber" (ngModelChange)="setPageNumber($event)">
        <option *ngFor="let page of pageNumbers" [ngValue]="page">{{ page }}</option>
      </select>
      of {{ pageCount }}
    </label>
    <label class="table-toolbar-control">
      Records per page
      <select [ngModel]="pageSize" (ngModelChange)="setPageSize($event)">
        <option *ngFor="let size of pageSizeOptions" [ngValue]="size">{{ size }}</option>
      </select>
    </label>
    <button type="button" mat-stroked-button (click)="exportCsv()">
      <mat-icon>download</mat-icon>
      Export
    </button>
    <input #csvFileInput type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" class="visually-hidden"
      (change)="importCsv($event)" />
    <button type="button" mat-stroked-button (click)="csvFileInput.click()">
      <mat-icon>upload_file</mat-icon>
      Import
    </button>
    <span class="table-toolbar-separator" role="separator" aria-orientation="vertical"></span>
    <button type="button" mat-stroked-button (click)="addNewRow()">
      <mat-icon>add</mat-icon>
      Add New Row
    </button>
    <button type="button" mat-stroked-button class="save-changes-button"
      [class.save-changes-pending]="pendingRowCount > 0" (click)="saveChanges()">
      <mat-icon>save</mat-icon>
      Save Changes<span *ngIf="pendingRowCount > 0"> ({{ pendingRowCount }})</span>
    </button>
    <button *ngIf="pendingRowCount > 0" type="button" mat-stroked-button (click)="discardChanges()">
      <mat-icon>undo</mat-icon>
      Discard Changes
    </button>
    <label class="show-changes-control">
      <input type="checkbox" [ngModel]="showOnlyChanges"
        (ngModelChange)="onShowOnlyChangesChange($event)" />
      Show Changes Only
    </label>
    <label class="show-changes-control">
      <input type="checkbox" [(ngModel)]="showAlternateUnits" />
      Show Alternate Units
    </label>
  </div>

</div>
<div *ngIf="mode === 'enums'" class="enum-mode">
  <div class="enum-mode-header">
    <label class="enum-type-selector">
      <span>Enum</span>
      <select [ngModel]="selectedEnumType" (ngModelChange)="onEnumTypeChange($event)">
        <option *ngFor="let type of enumTypes" [ngValue]="type">{{ type }}</option>
      </select>
    </label>
    <button type="button" mat-stroked-button (click)="openNewEnumDialog(newEnumDialog)">
      <mat-icon>add</mat-icon>
      Add New Enum
    </button>
  </div>

  <div class="enum-editor-actions" *ngIf="selectedEnumType">
    <button type="button" mat-stroked-button (click)="addEnumItem()" [disabled]="!enumValuesLoaded || enumSaving">
      <mat-icon>add</mat-icon>
      Add Value
    </button>
    <span class="enum-action-spacer"></span>
    <button type="button" mat-stroked-button (click)="resetCurrentEnum()" [disabled]="!enumValuesLoaded || enumSaving">
      <mat-icon>restart_alt</mat-icon>
      Reset
    </button>
    <button type="button" mat-flat-button color="primary" (click)="saveCurrentEnum()" [disabled]="!enumValuesLoaded || enumSaving">
      <mat-icon>save</mat-icon>
      Save
    </button>
  </div>

  <div class="enum-values-scroll" *ngIf="selectedEnumType">
    <table class="enum-values-table">
      <thead>
        <tr>
          <th>Code ID</th>
          <th>Name (Code Enum Member)</th>
          <th>Label (Code Description)</th>
          <th>Abbreviation</th>
          <th>Description</th>
          <th>In Tools</th>
          <th>Tools Namespace</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let item of enumItems; let index = index">
          <td><input type="number" step="1" [(ngModel)]="item.CodeId" aria-label="Code ID" /></td>
          <td><input type="text" [(ngModel)]="item.Name" aria-label="Name (Code Enum Member)" /></td>
          <td><input type="text" [(ngModel)]="item.Label" aria-label="Label (Code Description)" /></td>
          <td><input type="text" [(ngModel)]="item.Abbreviation" aria-label="Abbreviation" /></td>
          <td><input type="text" [(ngModel)]="item.Description" aria-label="Description" /></td>
          <td class="enum-tools-flag"><input type="checkbox" [(ngModel)]="item.IsInTools" [attr.aria-label]="'In Tools for ' + item.Name" /></td>
          <td><input type="text" [(ngModel)]="item.ToolsNamespace" aria-label="Tools Namespace" /></td>
          <td class="enum-row-actions">
            <button type="button" mat-icon-button title="Delete enum value"
              [attr.aria-label]="'Delete enum value ' + item.Name" (click)="removeEnumItem(index)">
              <mat-icon>delete</mat-icon>
            </button>
          </td>
        </tr>
        <tr *ngIf="!enumItems.length">
          <td colspan="8" class="enum-empty-state">No values yet. Add a value to this enum.</td>
        </tr>
      </tbody>
    </table>
  </div>

  <p *ngIf="!selectedEnumType && !enumTypes.length" class="enum-empty-state">No enums are available.</p>

  <ng-template #newEnumDialog>
    <h2 mat-dialog-title>Add New Enum</h2>
    <mat-dialog-content class="new-enum-form">
      <label>
        <span>Enum name</span>
        <input type="text" [(ngModel)]="newEnumTypeName" (keyup.enter)="createEnumType()" />
      </label>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" mat-button mat-dialog-close>Cancel</button>
      <button type="button" mat-stroked-button (click)="createEnumType()"
        [disabled]="!newEnumTypeName.trim() || enumSaving">Create Enum</button>
    </mat-dialog-actions>
  </ng-template>
</div>

<div *ngIf="mode === 'tableEditor'" class="enum-mode">
  <div class="enum-mode-header">
    <label class="enum-type-selector">
      <span>Table</span>
      <select [ngModel]="selectedTableEditor" (ngModelChange)="onTableEditorTableChange($event)">
        <option *ngIf="tableEditorDraft?.Id === 0" [ngValue]="tableEditorDraft">New table</option>
        <option *ngFor="let item of tables" [ngValue]="item">{{ variableToDisplay(item.Name) }}</option>
      </select>
    </label>
    <button type="button" mat-stroked-button (click)="addNewEditorTable()">
      <mat-icon>add</mat-icon>
      Add New Table
    </button>
  </div>

  <div class="table-editor-details" *ngIf="tableEditorDraft as editTable">
    <label>
      <span>Name</span>
      <input type="text" [(ngModel)]="editTable.Name" aria-label="Table name" />
    </label>
    <label>
      <span>Description</span>
      <input type="text" [(ngModel)]="editTable.Description" aria-label="Table description" />
    </label>
  </div>

  <div class="enum-editor-actions" *ngIf="tableEditorDraft">
    <button type="button" mat-stroked-button (click)="addTableEditorColumn()"
      [disabled]="tableEditorSaving || tableEditorColumnsLoading">
      <mat-icon>add</mat-icon>
      Add Column
    </button>
    <span class="enum-action-spacer"></span>
    <button type="button" mat-stroked-button (click)="resetTableEditor()"
      [disabled]="tableEditorSaving || tableEditorColumnsLoading">
      <mat-icon>restart_alt</mat-icon>
      Reset
    </button>
    <button type="button" mat-flat-button color="primary" (click)="saveTableEditor()"
      [disabled]="!tableEditorDraft.Name.trim() || tableEditorSaving || tableEditorColumnsLoading">
      <mat-icon>save</mat-icon>
      Save
    </button>
  </div>

  <div class="enum-values-scroll" *ngIf="tableEditorDraft">
    <table class="enum-values-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Description</th>
          <th>Type</th>
          <th>Enum Type</th>
          <th>Measurement</th>
          <th>Unit</th>
          <th>Decimal Digits</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let column of tableEditorColumns; let index = index">
          <td><input type="text" [(ngModel)]="column.Name" aria-label="Column name" /></td>
          <td><input type="text" [(ngModel)]="column.Description" aria-label="Column description" /></td>
          <td>
            <select [(ngModel)]="column.TypeId" aria-label="Column type">
              <option *ngFor="let option of columnTypeOptions" [ngValue]="option.id">{{ option.label }}</option>
            </select>
          </td>
          <td>
            <select *ngIf="column.TypeId === ColumnType.Enum" [(ngModel)]="column.EnumType" aria-label="Enum type">
              <option [ngValue]="''"></option>
              <option *ngFor="let type of enumTypes" [ngValue]="type">{{ type }}</option>
            </select>
          </td>
          <td>
            <select *ngIf="column.TypeId === ColumnType.Double" [ngModel]="column.ContextId"
              (ngModelChange)="onTableEditorContextChange(column, $event)" aria-label="Measurement context">
              <option [ngValue]="null"></option>
              <option *ngFor="let option of measurementContextOptions" [ngValue]="option.id">{{ option.label }}</option>
            </select>
          </td>
          <td>
            <select *ngIf="column.TypeId === ColumnType.Double" [(ngModel)]="column.UnitId" aria-label="Unit type">
              <option [ngValue]="null"></option>
              <option *ngFor="let option of getUnitOptionsForContext(column.ContextId)" [ngValue]="option.id">{{ option.label }}</option>
            </select>
          </td>
          <td>
            <input *ngIf="column.TypeId === ColumnType.Double || column.TypeId === ColumnType.Decimal"
              type="number" [(ngModel)]="column.DecimalDigits" aria-label="Decimal digits" />
          </td>
          <td class="enum-row-actions">
            <button type="button" mat-icon-button title="Delete column"
              [attr.aria-label]="'Delete column ' + column.Name" (click)="removeTableEditorColumn(index)"
              [disabled]="tableEditorSaving || tableEditorColumnsLoading">
              <mat-icon>delete</mat-icon>
            </button>
          </td>
        </tr>
        <tr *ngIf="!tableEditorColumnsLoading && !tableEditorColumns.length">
          <td colspan="8" class="enum-empty-state">No columns are defined for this table.</td>
        </tr>
      </tbody>
    </table>
  </div>

  <p *ngIf="!tables.length && !tableEditorDraft" class="enum-empty-state">No tables are available.</p>
</div>